import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
    Activity, BellRing, Bot, Calculator, ChevronRight, CircleDollarSign,
    Download, Eye, FileKey, FileText, KeyRound, Landmark, LayoutDashboard,
    ListChecks, LockKeyhole, LogOut, Menu, MessageSquareText, MonitorCog,
    ReceiptText, Send, Settings, ShieldCheck, Smartphone, Users, WalletCards,
    X
} from 'lucide-react';
import socketService from '../services/socketService';
import ResilientImage from './ResilientImage';
import './Layout.css';

const Layout = ({ children }) => {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const username = localStorage.getItem('adminUsername') || 'Admin';
    const whatsappLink = 'https://wa.me/88017xxxxxxxx';
    const location = useLocation();
    const navigate = useNavigate();
    const isMainAdmin = localStorage.getItem('isMaster') === 'true';

    let permissions = {};
    try {
        permissions = JSON.parse(localStorage.getItem('permissions') || '{}');
    } catch (error) {
        permissions = {};
    }

    const hasPermission = (key) => isMainAdmin || permissions[key] === true;

    const handleLogout = () => {
        try {
            if (socketService && typeof socketService.disconnect === 'function') {
                socketService.disconnect();
            }
        } catch (error) {
            console.error('Logout Error:', error);
        }
        localStorage.clear();
        sessionStorage.clear();
        navigate('/');
        window.location.reload();
    };

    useEffect(() => {
        const socket = socketService.getSocket();
        if (!socket) return undefined;

        const handleForceLogout = (data) => {
            alert(data?.message || 'Permission changed. Please sign in again.');
            handleLogout();
        };
        const handleGlobalLogout = (data) => {
            alert(data?.message || 'Security update: please sign in again.');
            handleLogout();
        };

        socket.on('forceLogout', handleForceLogout);
        socket.on('force_logout_global', handleGlobalLogout);
        return () => {
            socket.off('forceLogout', handleForceLogout);
            socket.off('force_logout_global', handleGlobalLogout);
        };
    }, []);

    useEffect(() => {
        setSidebarOpen(false);
        window.scrollTo({ top: 0, behavior: 'auto' });
    }, [location.pathname]);

    useEffect(() => {
        document.body.classList.toggle('gp-sidebar-open', sidebarOpen);
        return () => document.body.classList.remove('gp-sidebar-open');
    }, [sidebarOpen]);

    const menuGroups = useMemo(() => [
        {
            label: 'Overview',
            items: [
                { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, show: true },
                { to: '/users', label: 'Users', icon: Users, show: hasPermission('permissonUserlist') },
                { to: '/user_devices', label: 'Devices', icon: Smartphone, show: hasPermission('permissonUserdevices') }
            ]
        },
        {
            label: 'Payments',
            items: [
                { to: '/mobile_banking', label: 'Banking Accounts', icon: Landmark, show: hasPermission('permissonMobileBank') },
                { to: '/pending_list', label: 'Payment Send', icon: Send, show: hasPermission('permissonSendPayment') },
                { to: '/payment_receive', label: 'Payment Received', icon: ReceiptText, show: hasPermission('permissonPaymentReceived') },
                { to: '/app_transactions', label: 'App Transactions', icon: WalletCards, show: hasPermission('apptransactionscheck') },
                { to: '/job_tracker', label: 'Job Tracker', icon: Activity, show: hasPermission('permissonJobTracker') },
                { to: '/account_tasks', label: 'Account Manager', icon: ListChecks, show: hasPermission('permissonAccount') },
                { to: '/NumberCalculator', label: 'Calculator', icon: Calculator, show: true }
            ]
        },
        {
            label: 'Messages & Apps',
            items: [
                { to: '/smsget', label: 'Get SMS', icon: BellRing, show: hasPermission('permissonGetsms') },
                { to: '/sms', label: 'Add SMS', icon: MessageSquareText, show: hasPermission('permissonAddsms') },
                { to: '/app_receive', label: 'App SMS', icon: Smartphone, show: hasPermission('permissonAPPreceive') },
                { to: '/codes', label: 'Security Codes', icon: FileKey, show: hasPermission('permissonCode') }
            ]
        },
        {
            label: 'Configuration',
            items: [
                { to: '/api_keys', label: 'API Configuration', icon: KeyRound, show: hasPermission('permissonAPIkey') },
                { to: '/master_keys', label: 'Master Keys', icon: ShieldCheck, show: isMainAdmin },
                { to: '/api_logs', label: 'API View', icon: FileText, show: hasPermission('permissonApiview') },
                { to: '/otp_view', label: 'OTP View', icon: LockKeyhole, show: isMainAdmin },
                { to: '/deposit_monitor', label: 'Deposit View', icon: MonitorCog, show: isMainAdmin },
                { to: '/bot_codes', label: 'Bot API', icon: Bot, show: isMainAdmin },
                { to: '/device-setup', label: 'Device Setup', icon: Smartphone, show: true },
                { to: '/change_password', label: 'Change Password', icon: LockKeyhole, show: true },
                { to: '/update_seen', label: 'Update Seen Status', icon: Eye, show: hasPermission('permissonSeenBy') },
                { to: '/commission_setting', label: 'Commission', icon: CircleDollarSign, show: isMainAdmin },
                { href: '/debit_security/master', label: 'Dabit Security', icon: ShieldCheck, show: isMainAdmin },
                { href: '/Dabit Pay Live API Documentati.pdf', label: 'Download API Docs', icon: Download, show: true, download: 'Dabit_Pay_Live_API_Documentation.pdf' }
            ]
        }
    ], [isMainAdmin, permissions]);

    const activeLabel = menuGroups
        .flatMap((group) => group.items)
        .find((item) => item.to === location.pathname)?.label || 'Admin Portal';

    return (
        <div className="gp-shell">
            <header className="gp-topbar">
                <div className="gp-topbar-left">
                    <button
                        type="button"
                        className="gp-menu-button"
                        onClick={() => setSidebarOpen((open) => !open)}
                        aria-label="Toggle navigation"
                        aria-expanded={sidebarOpen}
                    >
                        {sidebarOpen ? <X size={21} /> : <Menu size={21} />}
                    </button>
                    <Link to="/dashboard" className="gp-brand" aria-label="GP Pay dashboard">
                        <ResilientImage src="/logo.png" alt="GP Pay" className="gp-brand-logo" />
                        <span className="gp-brand-copy">
                            <strong>GP Pay</strong>
                            <small>Payment Operations</small>
                        </span>
                    </Link>
                    <span className="gp-page-context">{activeLabel}</span>
                </div>

                <div className="gp-topbar-actions">
                    <div className="gp-live-status"><span /> Live system</div>
                    <div className="gp-profile">
                        <span className="gp-avatar">{username.slice(0, 1).toUpperCase()}</span>
                        <span className="gp-profile-copy"><strong>{username}</strong><small>{isMainAdmin ? 'Master Admin' : 'Administrator'}</small></span>
                    </div>
                    <button type="button" onClick={handleLogout} className="gp-logout-button" title="Logout">
                        <LogOut size={17} /><span>Logout</span>
                    </button>
                </div>
            </header>

            <aside className={`gp-sidebar ${sidebarOpen ? 'is-open' : ''}`} aria-label="Primary navigation">
                <div className="gp-sidebar-scroll">
                    {menuGroups.map((group) => {
                        const visibleItems = group.items.filter((item) => item.show);
                        if (visibleItems.length === 0) return null;
                        return (
                            <section className="gp-nav-group" key={group.label}>
                                <p className="gp-nav-label">{group.label}</p>
                                <nav>
                                    {visibleItems.map((item) => {
                                        const Icon = item.icon || Settings;
                                        const isActive = item.to === location.pathname;
                                        const content = <><Icon size={18} /><span>{item.label}</span><ChevronRight className="gp-nav-arrow" size={15} /></>;
                                        if (item.href) {
                                            return <a key={item.label} href={item.href} download={item.download} className="gp-nav-link">{content}</a>;
                                        }
                                        return <Link key={item.to} to={item.to} className={`gp-nav-link ${isActive ? 'active' : ''}`}>{content}</Link>;
                                    })}
                                </nav>
                            </section>
                        );
                    })}
                </div>
                <div className="gp-sidebar-footer">
                    <span className="gp-security-icon"><ShieldCheck size={19} /></span>
                    <div><strong>Secure workspace</strong><small>Encrypted admin session</small></div>
                </div>
            </aside>

            {sidebarOpen && <button type="button" className="gp-sidebar-backdrop" onClick={() => setSidebarOpen(false)} aria-label="Close navigation" />}

            <main className="gp-main">
                <div className="gp-page-content">{children}</div>
            </main>

            <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="gp-support-float" aria-label="WhatsApp support">
                <ResilientImage src="/whatsapp.png" alt="WhatsApp" />
            </a>
        </div>
    );
};

export default Layout;
